import { useEffect, useState } from 'react'
import { Row, Col, Card, Statistic, Table, Tag } from 'antd'
import {
  UserOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  RiseOutlined
} from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import { statsApi, orderApi } from '../services/api'

function Dashboard() {
  const [stats, setStats] = useState({
    orders: 0,
    sales: 0,
    users: 0,
    products: 0
  })
  const [recentOrders, setRecentOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [salesData, setSalesData] = useState<{ dates: string[], amounts: number[] }>({ dates: [], amounts: [] })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes]: any = await Promise.allSettled([
          statsApi.getDashboard(),
          orderApi.getOrders({ page: 1, page_size: 5 })
        ])
        if (statsRes.status === 'fulfilled' && statsRes.value?.code === 200) {
          setStats(statsRes.value.data)
        }
        if (ordersRes.status === 'fulfilled' && ordersRes.value?.code === 200) {
          setRecentOrders(ordersRes.value.data.list || [])
        }
        // Fetch real 7-day sales data for chart
        const salesRes: any = await statsApi.getDailySales({ days: 7 })
        if (salesRes?.code === 200 && salesRes.data) {
          setSalesData({
            dates: salesRes.data.map((d: any) => d.date),
            amounts: salesRes.data.map((d: any) => d.amount || 0)
          })
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const statusMap: Record<number, { color: string; text: string }> = {
    0: { color: 'warning', text: '待支付' },
    1: { color: 'processing', text: '已支付' },
    2: { color: 'blue', text: '处理中' },
    3: { color: 'cyan', text: '配送中' },
    4: { color: 'success', text: '已完成' },
    5: { color: 'default', text: '已取消' }
  }

  const orderColumns = [
    { title: '订单编号', dataIndex: 'order_no', key: 'order_no', width: 180 },
    {
      title: '客户',
      key: 'customer',
      render: (_: any, r: any) => r.user?.phone || '-'
    },
    {
      title: '金额',
      dataIndex: 'final_amount',
      key: 'final_amount',
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: number) => {
        const s = statusMap[v] || { color: 'default', text: '未知' }
        return <Tag color={s.color}>{s.text}</Tag>
      }
    },
    {
      title: '下单时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => v ? new Date(v).toLocaleString() : '-'
    }
  ]

  const cardStyle = { borderRadius: 8 }
  const brand = '#55266c'

  // 销售趋势图表配置
  const salesChartOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: salesData.dates,
      boundaryGap: false
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: '¥{value}' }
    },
    series: [{
      name: '销售额',
      type: 'line',
      smooth: true,
      data: salesData.amounts,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(85, 38, 108, 0.3)' },
            { offset: 1, color: 'rgba(85, 38, 108, 0.05)' }
          ]
        }
      },
      lineStyle: { color: brand, width: 2 },
      itemStyle: { color: brand }
    }]
  }

  // 订单状态分布图表配置
  const orderStatusOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { orient: 'vertical', right: '5%', top: 'center' },
    series: [{
      name: '订单状态',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      emphasis: {
        label: { show: true, fontSize: 14, fontWeight: 'bold' }
      },
      labelLine: { show: false },
      data: [
        { value: 3, name: '待支付', itemStyle: { color: '#faad14' } },
        { value: 5, name: '已支付', itemStyle: { color: '#1890ff' } },
        { value: 2, name: '处理中', itemStyle: { color: '#597ef7' } },
        { value: 4, name: '配送中', itemStyle: { color: '#13c2c2' } },
        { value: 20, name: '已完成', itemStyle: { color: '#52c41a' } },
        { value: 1, name: '已取消', itemStyle: { color: '#d9d9d9' } }
      ]
    }]
  }

  return (
    <div>
      <h2 style={{ marginBottom: 24, color: '#1f2937' }}>数据概览</h2>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card style={cardStyle}>
            <Statistic
              title="商品总数"
              value={stats.products}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: brand }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={cardStyle}>
            <Statistic
              title="注册用户"
              value={stats.users}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={cardStyle}>
            <Statistic
              title="订单总数"
              value={stats.orders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#f59e0b' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={cardStyle}>
            <Statistic
              title="总销售额"
              value={stats.sales}
              prefix={<DollarOutlined />}
              suffix="元"
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <Card title="销售趋势（近7天）" style={cardStyle}>
            <ReactECharts option={salesChartOption} style={{ height: 280 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="订单状态分布" style={cardStyle}>
            <ReactECharts option={orderStatusOption} style={{ height: 280 }} />
          </Card>
        </Col>
      </Row>

      <Card
        title={<span><RiseOutlined style={{ marginRight: 8, color: brand }} />最近订单</span>}
        style={cardStyle}
        extra={<a href="/orders" style={{ color: brand }}>查看全部</a>}
      >
        {recentOrders.length > 0 ? (
          <Table
            columns={orderColumns}
            dataSource={recentOrders}
            rowKey="id"
            pagination={false}
            size="small"
            loading={loading}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
            <ShoppingCartOutlined style={{ fontSize: 40, marginBottom: 12 }} />
            <div>暂无订单数据</div>
          </div>
        )}
      </Card>
    </div>
  )
}

export default Dashboard
